import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Search, AlertTriangle, TrendingUp, TrendingDown, Minus, Sparkles, Info,
  ChevronDown, ChevronRight, CheckCircle2, RotateCcw, ShieldCheck, X,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "2-digit" });
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function DeviationPill({ pct, isAnomaly }) {
  if (pct == null) return null;
  const abs = Math.abs(pct);
  if (abs < 0.01) {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-gray-100 text-gray-600"><Minus size={10} /> 0.00%</span>;
  }
  const up = pct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  let cls;
  if (isAnomaly) cls = up ? "bg-[#DCFCE7] text-[#15803D] ring-1 ring-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C] ring-1 ring-[#B91C1C]";
  else           cls = up ? "bg-[#DCFCE7]/60 text-[#15803D]"                   : "bg-[#FEE2E2]/60 text-[#B91C1C]";
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${cls}`}><Icon size={10} /> {up ? "+" : ""}{pct.toFixed(2)}%</span>;
}

function VerifyModal({ bill, partyName, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/rate-analysis/verify/${bill.txn_id}`, { verified: true, note });
      toast.success("Deviation marked verified");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="verify-modal">
      <form onSubmit={save} className="bg-white rounded-sm max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div className="small-label text-gray-500">Verify rate deviation</div>
            <div className="font-heading font-black text-lg">{partyName}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="small-label text-gray-500">Bill No</div><div className="data-num font-bold">{bill.bill_no || "—"}</div></div>
            <div><div className="small-label text-gray-500">Challan No</div><div className="data-num font-bold">{bill.challan_no || "—"}</div></div>
            <div><div className="small-label text-gray-500">Date</div><div className="font-semibold">{fmtDate(bill.date)}</div></div>
            <div><div className="small-label text-gray-500">₹ / kg</div><div className="data-num font-bold text-[#B45309]">₹ {bill.rate_per_kg.toFixed(2)}</div></div>
          </div>
          <div>
            <label className="small-label block mb-2">Verification note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="input-lg"
              placeholder="e.g. confirmed with customer over WhatsApp"
              data-testid="verify-note-input" autoFocus />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50">
          <button type="button" onClick={onClose} className="h-9 px-4 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary h-9 px-4 flex items-center gap-1.5" data-testid="verify-save-btn">
            <ShieldCheck size={14} /> {busy ? "Saving…" : "Mark verified"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CustomerList({ list, threshold, selectedId, onSelect, search, setSearch, suggestedCount, totalPending, filterPending, setFilterPending }) {
  const filtered = useMemo(() => {
    let arr = list;
    if (filterPending) arr = arr.filter((r) => r.pending_verify_count > 0);
    const q = search.trim().toLowerCase();
    if (q) arr = arr.filter((r) => r.party_name.toLowerCase().includes(q) || (r.phone || "").toLowerCase().includes(q));
    return arr;
  }, [list, search, filterPending]);

  return (
    <div className="card-flat flex flex-col h-full min-h-[70vh]">
      <div className="p-3 border-b border-gray-200 space-y-2">
        <div className="flex items-center justify-between">
          <div className="small-label text-gray-500">Customers ({list.length})</div>
          {suggestedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#B91C1C]" data-testid="ra-suggested-count">
              <Sparkles size={11} /> {suggestedCount} to review
            </span>
          )}
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer…" className="input-lg pl-8 h-9 text-sm w-full"
            data-testid="ra-search" />
        </div>
        <button
          onClick={() => setFilterPending(!filterPending)}
          className={`w-full text-left px-2.5 py-1.5 rounded-sm text-[11px] font-bold border flex items-center justify-between ${filterPending ? "bg-[#FEF3C7] border-[#B45309] text-[#B45309]" : "bg-white border-gray-200 text-gray-600 hover:border-black"}`}
          data-testid="ra-filter-pending">
          <span className="inline-flex items-center gap-1">
            <AlertTriangle size={12} /> Pending verification
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${filterPending ? "bg-[#B45309] text-white" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>{totalPending}</span>
        </button>
      </div>
      <div className="overflow-auto flex-1">
        {filtered.length === 0 && <div className="p-6 text-center text-xs text-gray-400">No customers</div>}
        {filtered.map((r) => (
          <button key={r.party_id}
            onClick={() => onSelect(r.party_id)}
            className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 ${selectedId === r.party_id ? "bg-black text-white hover:bg-black" : ""}`}
            data-testid={`ra-customer-${r.party_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {r.suggested && <Sparkles size={11} className={selectedId === r.party_id ? "text-[#FDE68A]" : "text-[#B91C1C]"} />}
                  <span className="truncate">{r.party_name}</span>
                </div>
                <div className={`text-[10px] mt-0.5 ${selectedId === r.party_id ? "text-gray-300" : "text-gray-500"}`}>
                  {r.bills} bills · median ₹{r.median_rate}/kg
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                {r.pending_verify_count > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5 ${selectedId === r.party_id ? "bg-[#FDE68A] text-black" : "bg-[#FEF3C7] text-[#B45309]"}`}
                    title={`${r.pending_verify_count} bill${r.pending_verify_count === 1 ? "" : "s"} pending verification`}>
                    <AlertTriangle size={9} /> {r.pending_verify_count}
                  </span>
                )}
                {r.anomaly_count > 0 && r.pending_verify_count === 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5 ${selectedId === r.party_id ? "bg-white text-[#15803D]" : "bg-[#DCFCE7] text-[#15803D]"}`}
                    title="All anomalies verified">
                    <CheckCircle2 size={9} /> {r.anomaly_count}
                  </span>
                )}
              </div>
            </div>
            <div className={`text-[10px] mt-1 ${selectedId === r.party_id ? "text-gray-300" : "text-gray-400"}`}>
              spread {r.spread_pct}% · cv {r.cv_pct}%
            </div>
          </button>
        ))}
      </div>
      <div className="border-t border-gray-100 p-2 text-[10px] text-gray-400 leading-tight">
        <Sparkles size={10} className="inline text-[#B91C1C] mr-0.5" />
        <b>Suggested to review</b> = 2+ anomaly bills or CV ≥ 8%.
        Anomaly = deviates &gt; {threshold}% from that customer&apos;s median.
      </div>
    </div>
  );
}

function BillsPanel({ detail, loading, onVerifyClick, onUnverify }) {
  const [expanded, setExpanded] = useState({});
  const [showOnlyPending, setShowOnlyPending] = useState(false);

  if (loading) return <div className="card-flat p-6 text-center text-gray-400">Loading…</div>;
  if (!detail) return (
    <div className="card-flat p-10 text-center text-gray-400 min-h-[70vh] flex flex-col items-center justify-center gap-3">
      <Info size={32} className="text-gray-300" />
      <div className="text-sm">Select a customer on the left to see every bill&apos;s ₹/kg rate and its deviation.</div>
      <div className="text-[11px] text-gray-400 max-w-md">Suggested customers (with the sparkle icon) have the most rate deviations and are worth checking first.</div>
    </div>
  );

  const { party, median_rate, min_rate, max_rate, bills_count, anomaly_count, pending_verify_count, anomaly_threshold_pct, bills } = detail;
  const spread_pct = median_rate > 0 ? ((max_rate - min_rate) / median_rate) * 100 : 0;
  const visibleBills = showOnlyPending ? bills.filter((b) => b.is_anomaly && !b.rate_deviation_verified) : bills;

  return (
    <div className="card-flat" data-testid="ra-detail">
      <div className="p-4 border-b border-gray-200 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="small-label text-gray-500">Rate deviation · per bill</div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <Link to={`/parties/${party.id}`} target="_blank" rel="noopener"
              className="font-heading font-black text-2xl hover:underline decoration-2 underline-offset-2"
              data-testid="ra-detail-party-name">
              {party.name}
            </Link>
            {pending_verify_count > 0 ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold bg-[#FEF3C7] text-[#B45309]" data-testid="ra-pending-badge">
                <AlertTriangle size={11} /> {pending_verify_count} pending verification
              </span>
            ) : anomaly_count > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold bg-[#DCFCE7] text-[#15803D]" data-testid="ra-verified-badge">
                <CheckCircle2 size={11} /> All {anomaly_count} deviations verified
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {anomaly_count > 0 && (
            <button
              onClick={() => setShowOnlyPending((v) => !v)}
              className={`px-2.5 h-8 rounded-sm text-[11px] font-semibold border ${showOnlyPending ? "bg-[#B45309] text-white border-[#B45309]" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid="ra-toggle-pending-only">
              {showOnlyPending ? "Showing pending only" : "Show pending only"}
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-b border-gray-200">
        <div className="p-3 border-r border-gray-200"><div className="small-label text-gray-500">Median ₹/kg</div><div className="data-num text-lg font-bold">₹ {median_rate}</div></div>
        <div className="p-3 border-r border-gray-200"><div className="small-label text-gray-500">Lowest</div><div className="data-num text-lg font-bold text-[#B91C1C]">₹ {min_rate}</div></div>
        <div className="p-3 border-r border-gray-200"><div className="small-label text-gray-500">Highest</div><div className="data-num text-lg font-bold text-[#15803D]">₹ {max_rate}</div></div>
        <div className="p-3"><div className="small-label text-gray-500">Spread · {bills_count} bills</div><div className="data-num text-lg font-bold">{spread_pct.toFixed(1)}%</div></div>
      </div>

      <div className="table-wrap">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Challan No</th>
              <th className="text-left">Bill No</th>
              <th className="num">KG</th>
              <th className="num">Amount ₹</th>
              <th className="num">₹ / kg</th>
              <th className="text-left">Deviation</th>
              <th className="text-left">Verify</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {visibleBills.length === 0 && (
              <tr><td colSpan="9" className="text-center py-8 text-gray-400 text-sm">{showOnlyPending ? "No pending verifications" : "No bills with computable ₹/kg."}</td></tr>
            )}
            {visibleBills.map((b) => {
              const opened = !!expanded[b.txn_id];
              const highlight = b.is_anomaly && !b.rate_deviation_verified;
              const verifiedOK = b.is_anomaly && b.rate_deviation_verified;
              return (
                <React.Fragment key={b.txn_id}>
                  <tr className={highlight ? "bg-[#FEF3C7]/60" : verifiedOK ? "bg-[#DCFCE7]/30" : ""} data-testid={`ra-bill-${b.txn_id}`}>
                    <td className="whitespace-nowrap">{fmtDate(b.date)}</td>
                    <td>
                      <div className={`data-num font-bold ${highlight ? "text-[#B45309]" : ""}`} data-testid={`ra-challan-${b.txn_id}`}>{b.challan_no || "—"}</div>
                      {b.from_godown && <div className="text-[10px] text-gray-500">{b.from_godown}</div>}
                    </td>
                    <td>
                      <div className={`data-num font-bold ${highlight ? "text-[#B45309]" : "text-gray-800"}`} data-testid={`ra-bill-no-${b.txn_id}`}>{b.bill_no || "—"}</div>
                      {b.bill_amount != null && b.bill_amount !== "" && <div className="text-[10px] text-gray-500 data-num">₹ {formatMoney(b.bill_amount)}</div>}
                    </td>
                    <td className="num">{formatMoney(b.total_kg)}</td>
                    <td className="num">{formatMoney(b.total_amount)}</td>
                    <td className="num">
                      <span className={`data-num text-base font-black ${highlight ? "text-[#B45309]" : "text-gray-900"}`} data-testid={`ra-rpk-${b.txn_id}`}>
                        {b.rate_per_kg.toFixed(2)}
                      </span>
                    </td>
                    <td><DeviationPill pct={b.deviation_pct} isAnomaly={b.is_anomaly} /></td>
                    <td>
                      {b.is_anomaly ? (
                        b.rate_deviation_verified ? (
                          <div className="flex items-start gap-1 flex-col">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-[#DCFCE7] text-[#15803D]" data-testid={`ra-verified-${b.txn_id}`}>
                              <ShieldCheck size={11} /> Verified
                            </span>
                            <div className="text-[9px] text-gray-500 leading-tight">
                              {b.rate_deviation_verified_by && <div>{b.rate_deviation_verified_by}</div>}
                              {b.rate_deviation_verified_at && <div>{fmtWhen(b.rate_deviation_verified_at)}</div>}
                              {b.rate_deviation_verified_note && <div className="italic">&quot;{b.rate_deviation_verified_note}&quot;</div>}
                            </div>
                            <button onClick={() => onUnverify(b)}
                              className="text-[10px] text-gray-500 hover:text-[#B91C1C] inline-flex items-center gap-0.5 mt-0.5"
                              data-testid={`ra-unverify-${b.txn_id}`}>
                              <RotateCcw size={10} /> undo
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => onVerifyClick(b)}
                            className="btn-primary h-7 px-2 text-[11px] flex items-center gap-1"
                            data-testid={`ra-verify-btn-${b.txn_id}`}>
                            <ShieldCheck size={12} /> Verify
                          </button>
                        )
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => setExpanded((s) => ({ ...s, [b.txn_id]: !s[b.txn_id] }))}
                        className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-0.5"
                        data-testid={`ra-expand-${b.txn_id}`}>
                        {opened ? <ChevronDown size={12} /> : <ChevronRight size={12} />} items
                      </button>
                    </td>
                  </tr>
                  {opened && (
                    <tr>
                      <td colSpan="9" className="bg-gray-50 border-b border-gray-100">
                        <div className="p-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500">
                                <th className="text-left font-semibold pb-1">Brand</th>
                                <th className="text-left font-semibold pb-1">Size</th>
                                <th className="num font-semibold pb-1">Qty</th>
                                <th className="num font-semibold pb-1">Rate ₹</th>
                                <th className="num font-semibold pb-1">Line ₹</th>
                              </tr>
                            </thead>
                            <tbody>
                              {b.items.map((it, i) => (
                                <tr key={i}>
                                  <td>{it.brand}</td>
                                  <td>{it.size}</td>
                                  <td className="num">{it.filled_qty}</td>
                                  <td className="num">{it.rate.toFixed(2)}</td>
                                  <td className="num">{formatMoney(it.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RateAnalysis() {
  const [meta, setMeta] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterPending, setFilterPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [verifyBill, setVerifyBill] = useState(null);

  const loadMeta = async () => {
    try {
      const { data } = await api.get("/rate-analysis/customers");
      setMeta(data);
      if (!selectedId) {
        const first = (data.rows || []).find((r) => r.pending_verify_count > 0) || (data.rows || []).find((r) => r.suggested) || (data.rows || [])[0];
        if (first) setSelectedId(first.party_id);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadDetail = async (pid) => {
    if (!pid) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/rate-analysis/${pid}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); setDetail(null); }
    finally { setLoadingDetail(false); }
  };

  useEffect(() => {
    (async () => { setLoading(true); await loadMeta(); setLoading(false); })();
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId]);

  const suggested = useMemo(() => (meta?.rows || []).filter((r) => r.suggested).length, [meta]);
  const totalPending = meta?.total_pending_verifications || 0;

  const handleUnverify = async (b) => {
    if (!window.confirm("Remove verification and mark this deviation as pending again?")) return;
    try {
      await api.patch(`/rate-analysis/verify/${b.txn_id}`, { verified: false });
      toast.success("Marked pending");
      await Promise.all([loadMeta(), loadDetail(selectedId)]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="rate-analysis-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="small-label text-gray-500">Rate Analysis</div>
          <h2 className="font-heading font-black text-2xl">Per-bill ₹/kg & deviation detection</h2>
          <div className="text-xs text-gray-500 mt-1 max-w-3xl">
            For every customer we show the effective ₹/kg on each bill and how it deviates from that customer&apos;s median rate.
            Bills beyond ±{meta?.anomaly_threshold_pct || 5}% are flagged. Users can <b>Verify</b> each deviation once
            it&apos;s been confirmed with the customer. The customer name in the detail panel is a link to their ledger.
          </div>
        </div>
        {totalPending > 0 && (
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border-2 border-[#B45309] bg-[#FEF3C7] text-[#B45309]" data-testid="ra-global-pending">
            <AlertTriangle size={16} />
            <div>
              <div className="small-label text-[#B45309]">Pending verifications</div>
              <div className="data-num text-lg font-black leading-tight">{totalPending}</div>
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <CustomerList
          list={meta?.rows || []}
          threshold={meta?.anomaly_threshold_pct || 5}
          selectedId={selectedId}
          onSelect={setSelectedId}
          search={search}
          setSearch={setSearch}
          suggestedCount={suggested}
          totalPending={totalPending}
          filterPending={filterPending}
          setFilterPending={setFilterPending}
        />
        {loading ? (
          <div className="card-flat p-6 text-center text-gray-400 min-h-[70vh] flex items-center justify-center">Loading…</div>
        ) : (
          <BillsPanel detail={detail} loading={loadingDetail}
            onVerifyClick={setVerifyBill}
            onUnverify={handleUnverify} />
        )}
      </div>
      {verifyBill && detail && (
        <VerifyModal
          bill={verifyBill}
          partyName={detail.party.name}
          onClose={() => setVerifyBill(null)}
          onSaved={async () => { setVerifyBill(null); await Promise.all([loadMeta(), loadDetail(selectedId)]); }}
        />
      )}
    </div>
  );
}
